import type { InferenceSession } from 'onnxruntime-web';
import type { AppConfig, Backend } from './types';

/** `onnxruntime-web` と `onnxruntime-web/wasm` は同じ API 形状。型は既定エントリのものを使う。 */
type Ort = typeof import('onnxruntime-web');

export class DetectorError extends Error {}

export interface DetectorProgress {
  stage: 'download' | 'init' | 'warmup';
  backend: Backend;
  loaded?: number;
  total?: number;
}

export interface Detector {
  readonly backend: Backend;
  readonly threads: number;
  readonly inputSize: number;
  run(input: Float32Array): Promise<{ data: Float32Array; dims: number[] }>;
  release(): Promise<void>;
}

const CDN_WASM_DIR = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${__ORT_VERSION__}/dist/`;
/**
 * 自前配信の WASM ランタイムの場所。同一オリジンの絶対 URL にする: Vite の開発サーバーは
 * 相対 URL の動的 import に `?import` を付けて変換し、ORT のランタイム読み込みが失敗するため
 * (絶対 URL は素通しされる。本番ビルドでは差はない)。
 */
const localWasmDir = (): string => new URL(`/ort/${__ORT_VERSION__}/`, self.location.href).href;

/** モデルを進捗付きで取得する。失敗は retries 回まで再試行(指数バックオフ)。 */
export async function fetchWithProgress(
  url: string,
  onProgress: (loaded: number, total: number | null) => void,
  fetchFn: typeof fetch = fetch,
  retries = 3,
): Promise<ArrayBuffer> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetchFn(url);
      if (!res.ok) throw new DetectorError(`model fetch ${res.status}: ${url}`);
      const len = res.headers.get('content-length');
      const total = len ? parseInt(len, 10) : null;
      if (!res.body) return await res.arrayBuffer();
      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.byteLength;
        onProgress(loaded, total);
      }
      const out = new Uint8Array(loaded);
      let off = 0;
      for (const c of chunks) { out.set(c, off); off += c.byteLength; }
      return out.buffer;
    } catch (e) {
      lastErr = e;
      if (attempt < retries - 1) await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
    }
  }
  throw lastErr instanceof Error ? lastErr : new DetectorError(String(lastErr));
}

/**
 * バックエンドごとに別エントリを読む。既定エントリ(WebGPU 込み)は 26.5 MB の jsep.wasm を要求し、
 * Cloudflare の 25 MiB 上限を超えるので CDN から。WASM 専用エントリは 13 MB の wasm を自前配信から。
 */
async function loadOrt(backend: Backend): Promise<Ort> {
  const ort: Ort = backend === 'webgpu' ? await import('onnxruntime-web') : await import('onnxruntime-web/wasm');
  ort.env.wasm.wasmPaths = backend === 'webgpu' ? CDN_WASM_DIR : localWasmDir();
  ort.env.wasm.numThreads = self.crossOriginIsolated ? Math.min(4, navigator.hardwareConcurrency || 1) : 1;
  return ort;
}

class OrtDetector implements Detector {
  readonly threads: number;
  constructor(
    private readonly ort: Ort,
    private readonly session: InferenceSession,
    readonly backend: Backend,
    readonly inputSize: number,
  ) {
    this.threads = ort.env.wasm.numThreads ?? 1;
  }

  async run(input: Float32Array): Promise<{ data: Float32Array; dims: number[] }> {
    const s = this.inputSize;
    const tensor = new this.ort.Tensor('float32', input, [1, 3, s, s]);
    const inName = this.session.inputNames[0]!;
    const outName = this.session.outputNames[0]!;
    const result = await this.session.run({ [inName]: tensor });
    const out = result[outName]!;
    return { data: out.data as Float32Array, dims: [...out.dims] };
  }

  async release(): Promise<void> {
    await this.session.release();
  }
}

/**
 * セッションを作成し、ウォームアップまで済ませた `Detector` を返す。ウォームアップが失敗した場合は
 * (WebGPU でセッション作成は成功するが実行時に失敗する、等)作成済みセッションを解放してから再送出する。
 */
export async function initBackend(ort: Ort, backend: Backend, buf: ArrayBuffer, inputSize: number): Promise<Detector> {
  const session = await ort.InferenceSession.create(buf, { executionProviders: [backend], graphOptimizationLevel: 'all' });
  const det = new OrtDetector(ort, session, backend, inputSize);
  try {
    await det.run(new Float32Array(3 * inputSize * inputSize));
    return det;
  } catch (e) {
    await session.release().catch(() => {});
    throw e;
  }
}

/** 既定の試行順: WebGPU が使える端末は WebGPU → WASM、それ以外は WASM のみ。 */
export function defaultAttempts(): Backend[] {
  return typeof navigator !== 'undefined' && 'gpu' in navigator ? ['webgpu', 'wasm'] : ['wasm'];
}

export async function createDetector(
  model: AppConfig['model'],
  onProgress: (p: DetectorProgress) => void,
  log: (m: string) => void,
  attempts: Backend[] = defaultAttempts(),
): Promise<Detector> {
  const buffers = new Map<string, ArrayBuffer>();
  let lastErr: unknown;
  for (const backend of attempts) {
    const url = backend === 'wasm' && model.wasmUrl ? model.wasmUrl : model.url;
    try {
      let buf = buffers.get(url);
      if (!buf) {
        buf = await fetchWithProgress(url, (loaded, total) => onProgress({ stage: 'download', backend, loaded, total: total ?? undefined }));
        buffers.set(url, buf);
      }
      onProgress({ stage: 'init', backend });
      const t0 = performance.now();
      const ort = await loadOrt(backend);
      onProgress({ stage: 'warmup', backend });
      const det = await initBackend(ort, backend, buf, model.inputSize);
      log(`detector ready: ${backend} threads=${det.threads} isolated=${self.crossOriginIsolated} model=${url} in ${Math.round(performance.now() - t0)} ms`);
      return det;
    } catch (e) {
      lastErr = e;
      log(`${backend} failed: ${(e as Error)?.message ?? e}`);
    }
  }
  throw new DetectorError(`no inference backend available: ${(lastErr as Error)?.message ?? lastErr}`);
}
